import { useEffect, useState } from "react";
import { Share, Plus, X, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { isIOS, isStandalone } from "@/lib/push";
import { getDeferredPrompt, clearDeferredPrompt } from "@/lib/pwaInstall";

const COPY = {
  mytrip: { name: "MyTrip", icon: "/images/icon-mytrip-192.png", bg: "#022455", line: "Add MyTrip to your Home Screen for one-tap access to your trips, payments and real-time alerts." },
  mycompass: { name: "MyCompass", icon: "/images/icon-mycompass-192.png", bg: "#002363", line: "Add MyCompass to your Home Screen for quick access and real-time alerts." },
};

export default function InstallPrompt({ brand = "mytrip", blocked = false }) {
  const c = COPY[brand] || COPY.mytrip;
  const key = `pwaTipSeen_${brand}`;
  const ios = isIOS();
  const [show, setShow] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (blocked || isStandalone() || localStorage.getItem(key)) return;
    const mobile = window.matchMedia("(max-width: 1023px)");
    let shown = false;
    const reveal = () => {
      if (shown || isStandalone() || localStorage.getItem(key) || !mobile.matches) return;
      shown = true;
      setShow(true);
      requestAnimationFrame(() => setMounted(true));
      localStorage.setItem(key, "1");
    };
    // Android/Chrome may fire beforeinstallprompt after our timer — reveal then too.
    const onInstallable = () => reveal();
    window.addEventListener("pwa-installable", onInstallable);
    const t = setTimeout(() => { if (ios || getDeferredPrompt()) reveal(); }, 1800);
    return () => { clearTimeout(t); window.removeEventListener("pwa-installable", onInstallable); };
  }, [key, ios, blocked]);

  if (!show) return null;

  const close = () => { setMounted(false); setTimeout(() => setShow(false), 250); };
  const install = async () => {
    const dp = getDeferredPrompt();
    if (!dp) return close();
    dp.prompt();
    try { await dp.userChoice; } catch { /* ignore */ }
    clearDeferredPrompt();
    close();
  };

  return (
    <div className="fixed inset-x-0 bottom-[4.5rem] z-[60] lg:hidden" data-testid={`pwa-install-tip-${brand}`}>
      <div className={cn("mx-auto max-w-md p-3 transition-transform duration-300 ease-out", mounted ? "translate-y-0" : "translate-y-[150%]")}>
        <div className="relative overflow-hidden rounded-2xl p-4 text-white shadow-2xl ring-1 ring-white/10" style={{ backgroundColor: c.bg }}>
          <button onClick={close} data-testid="pwa-install-dismiss" aria-label="Dismiss" className="absolute right-2.5 top-2.5 grid h-7 w-7 place-items-center rounded-full bg-white/10 text-white/80 transition-colors hover:bg-white/20"><X className="h-4 w-4" /></button>
          <div className="flex items-start gap-3 pr-7">
            <img src={c.icon} alt="" className="h-12 w-12 shrink-0 rounded-xl ring-1 ring-white/15" />
            <div className="min-w-0">
              <p className="text-sm font-semibold">Install {c.name}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-white/75">{c.line}</p>
            </div>
          </div>
          {ios ? (
            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-white/10 px-3 py-2.5 text-xs text-white/90">
              <span>Tap</span>
              <Share className="h-4 w-4" />
              <span>in your browser bar, then</span>
              <span className="inline-flex items-center gap-1 rounded-md bg-white/15 px-1.5 py-0.5 font-medium"><Plus className="h-3 w-3" /> Add to Home Screen</span>
            </div>
          ) : (
            <button onClick={install} data-testid="pwa-install-button" className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-white py-2.5 text-sm font-semibold transition-opacity hover:opacity-90" style={{ color: c.bg }}>
              <Download className="h-4 w-4" /> Add to Home Screen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
